import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { FileUploadInput } from '@/components/shared';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';

/**
 * 🚀 COMPONENTE SIGNED CONTRACTS FIELDSET CONTEXTUAL
 * 
 * Componente React Hook Form que utiliza a arquitetura unificada de schema.
 * Toda a lógica de validação, configuração contextual e defaults está
 * centralizada no arquivo SignedContractsFieldSetSchema.ts
 * 
 * CARACTERÍSTICAS:
 * - 100% React Hook Form
 * - Schema-driven configuration
 * - Zero duplicação de código
 * - Type safety completa
 * - Fieldset puro (sem botões de ação)
 * - Props explícitas (sem dependências de contexto)
 */

// Props do componente
interface SignedContractsFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: any; // Control do React Hook Form do formulário pai
    setValue: any; // SetValue do React Hook Form do formulário pai
    watch: any; // Watch do React Hook Form do formulário pai
    errors?: Record<string, any>; // Erros do formulário pai
    isSubmitting?: boolean; // Estado de submissão via props
    layout?: 'full' | 'compact'; // Layout via props
}

const SignedContractsFieldset: React.FC<SignedContractsFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // Obter valores específicos do React Hook Form
    const watchedWorkContractUrl = watch('workContractUrl');
    const watchedWorkContractFile = watch('workContractFile');

    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // File upload hook
    const { uploadStates, handleFileChange } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value: any) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // URL para o campo de upload (prioriza dados do formulário)
    const workContractUrl = watchedWorkContractUrl || formData.workContractUrl;

    // useEffect para atualizar URL quando mudar
    useEffect(() => {
        if (workContractUrl) {
            setValue('workContractUrl', workContractUrl);
        }
    }, [workContractUrl, setValue]);

    // Helper para verificar se campo deve aparecer usando schema unificado
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper para verificar se campo é obrigatório
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);



    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">Contrato Assinado</legend>
            <div className="pt-2">

                {/* Contrato de Trabalho / Prestação de Serviços */}
                {shouldShow('workContractFile') && (
                    <div>
                        <Controller
                            name="workContractFile"
                            control={control}
                            render={({ field, fieldState }) => (
                                <FileUploadInput
                                    name="workContractFile"
                                    label="Contrato de Trabalho / Prestação de Serviços"
                                    fileUrl={workContractUrl}
                                    fileName={watchedWorkContractFile?.name}
                                    isSubmitting={isSubmitting}
                                    isRequired={context === 'DP_UPDATE'}
                                    onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;

                                        field.onChange(file);
                                        await handleFileChange(e, {
                                            fieldName: 'workContractFile',
                                            urlFieldName: 'workContractUrl',
                                            pathPrefix: 'contracts',
                                            id: collaboratorId
                                        });
                                    }}
                                    progress={uploadStates['workContractFile']?.progress}
                                    status={uploadStates['workContractFile']?.status || 'idle'}
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                )}
            </div>
        </fieldset>
    );
};

export default SignedContractsFieldset;
